import { DashboardT } from 'business/definitions';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { RQAPICallWithData } from 'store/APIClient';
import { useDispatch } from 'store/hooks';
import { useGetDashboardFromSlugOrMetadata } from './useGetDashboardFromSlugOrMetadata';

const useDuplicateChart = (
    onUpdateSuccess?: (key: number, updatedProps: Partial<DashboardT>) => void,
) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const currentDashboard = useGetDashboardFromSlugOrMetadata();
    const { t } = useTranslation();

    return useMutation({
        mutationFn: async ({
            widgetKey,
            dashboardSlug,
            fromPosition,
        }: {
            widgetKey: number;
            dashboardSlug: string;
            fromPosition?: FromPositionQueryT;
        }) => {
            const response = await RQAPICallWithData<DashboardT>({
                endpoint: `explore/widget/dashboard/${widgetKey}/duplicate/${dashboardSlug}`,
                method: 'POST',
                body: { previousWidgetPosition: fromPosition },
            });
            return response.content.data;
        },

        onSuccess: (data, { widgetKey, dashboardSlug }) => {
            if (onUpdateSuccess) onUpdateSuccess(widgetKey, data);
            const isDuplicatingOnCurrentDashboard =
                currentDashboard?.slug === dashboardSlug;
            if (isDuplicatingOnCurrentDashboard)
                navigate(`/dashboard/${dashboardSlug}#widget-${widgetKey}`);
            dispatch.vision.notifyUser({
                message: '',
                snackAction: {
                    message: t('components:common.see'),
                    onClick: () => {
                        navigate(`/dashboard/${dashboardSlug}`);
                    },
                },
                snackType: 'success',
            });
        },

        onError: (error: Error) => {
            dispatch.vision.notifyUser({
                message: error.message,
                snackType: 'error',
            });
        },
    });
};

export default useDuplicateChart;
